/**
 * Trace + session correlation.
 *
 * traceId  = one-per-error-context. Sent as `X-Trace-Id` on outbound
 *            fetches so a server error is joinable with its client crash.
 * sessionId = one-per-browser-session. Persists in sessionStorage until
 *            the tab closes.
 *
 * The user-facing "reference code" is the first 8 chars of the traceId,
 * split with a dash for readability: `AB12-CD34`. Support pastes it into
 * the admin viewer, which searches by traceId prefix.
 */

const SESSION_STORAGE_KEY = 'obs.sessionId'
const TRACE_HEADER = 'x-trace-id'

/**
 * Generate a new traceId. Uses crypto.randomUUID when available;
 * falls back to a URL-safe random string.
 */
export function generateTraceId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  // Fallback — 32 hex chars, no dashes
  const bytes = new Uint8Array(16)
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256)
  }
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Convert a traceId to the human-readable 8-char reference code.
 * Example: `550e8400-e29b-41d4-a716-446655440000` → `550E-8400`.
 * The 8 chars come from the leading UUID segment, uppercased, so it
 * survives voice-over-phone support.
 */
export function toReferenceCode(traceId: string): string {
  const clean = traceId.replace(/-/g, '').slice(0, 8).toUpperCase()
  return `${clean.slice(0, 4)}-${clean.slice(4, 8)}`
}

/**
 * Reverse the reference-code display back to the raw prefix used for
 * `traceId LIKE 'prefix%'` search in the admin viewer.
 */
export function referenceCodeToPrefix(code: string): string {
  return code.replace(/[^0-9a-zA-Z]/g, '').toLowerCase()
}

/**
 * Session ID persists across page navigations within the same tab.
 * Cleared automatically on tab close.
 */
export function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return ''
  try {
    let sid = window.sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (!sid) {
      sid = generateTraceId()
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, sid)
    }
    return sid
  } catch {
    // sessionStorage can throw in privacy mode
    return ''
  }
}

export { TRACE_HEADER }
